import { Layout } from 'antd';
import { Content } from 'antd/lib/layout/layout';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { PdfContainer } from '../../common/pdf/PdfContainer';
import { ResultProperties } from '../../common/results/ResultProperties';
import Results from '../../common/results/Results';
import { ResultTables } from '../../common/results/ResultTables';
import { CustomSider } from '../../components/layout/CustomSider';
import { DetailContainer } from '../../components/layout/DetailContainer';
import { TopBar } from '../../components/layout/TopBar';
import { ErrorText } from '../../components/text/ErrorText';
import { getInferencePublic } from '../../lib/api/public-api';
import { useGetAlgorithm } from '../../lib/hooks';
import { getResultLabels } from '../../lib/labels';
import { useInterval } from '../../lib/useInterval';
import { processInferenceResult } from '../../lib/util';
import { isRecentAndProcessingInference } from '../../lib/util/processing';
import { reformInferenceToPdfFile } from '../../lib/util/reform';
import { isFinished } from '../../lib/util/status';
import useGlobal from '../../store/Store';
import { Inference, InferenceFile, PdfFile } from '../../types';

export const PublicDemoDetail = () => {
  let history = useHistory();

  const { inferenceId } = useParams() as {
    inferenceId: string;
  };

  const { algorithm } = useGetAlgorithm('9', true);

  const [ids] = useState<string[]>(localStorage.getItem('ids') ? JSON.parse(localStorage.getItem('ids') || '{}') : []);

  const inferenceFile = useRef<null | InferenceFile>(null);

  const [pdfFile, setPdfFile] = useState<PdfFile>();

  const [prevId, setPrevId] = useState<null | string>(null);
  const [nextId, setNextId] = useState<null | string>(null);
  const [inferenceList, setInferenceList] = useState<Inference[] | null>(null);
  const [error, setError] = useState<boolean>(false);

  const [globalState, globalActions] = useGlobal();

  useInterval(async () => {
    if (!inferenceList) return;
    try {
      updateInferenceListStatus(inferenceList);
    } catch (e) {}
  }, 2000);

  const reset = useCallback(() => {
    globalActions.setLabelOptions(null);
    globalActions.setLabelEdit(null);
    globalActions.removeLabels();
    inferenceFile.current = null;
  }, [globalActions, inferenceFile]);

  const loadLabels = useCallback(
    async (inference) => {
      const labels = getResultLabels(inference, inference.model);
      const filteredLabels = labels.filter((label) => label.exists);
      globalActions.setLabels(filteredLabels);
    },
    [globalActions]
  );

  const fetchInferences = useCallback(
    async (load: boolean) => {
      const results = await Promise.all(
        ids.map(async (id) => {
          let inf;
          try {
            inf = await getInferencePublic(id);
          } catch (e) {
            console.log(e);
            inf = 0;
          }
          return inf;
        })
      );
      const inferences: Inference[] = results.filter((i) => i !== 0) as Inference[];
      if (inferences.length === 0) {
        setError(true);
      } else {
        setError(false);
        setInferenceList(inferences);
      }
    },
    [ids]
  );

  const updateInferenceListStatus = useCallback(
    async (list: InferenceFile[]) => {
      const results = await Promise.all(
        ids.map(async (id) => {
          const inference = list && list.find((inference) => inference.id === id);
          return !inference || isRecentAndProcessingInference(inference) ? await getInferencePublic(id) : inference;
        })
      );
      setInferenceList(results);
    },
    [ids, setInferenceList]
  );

  useEffect(() => {
    globalActions.setReadOnly(true);
    fetchInferences(true);
    return () => {
      reset();
      globalActions.setReadOnly(false);
    };
  }, [inferenceId, globalActions, reset, fetchInferences]);

  useEffect(() => {
    const setupInferenceFile = async () => {
      if (inferenceList && inferenceId) {
        const inferenceInList = inferenceList.find((inference) => inference.id === inferenceId);

        // prevent loading loop
        if (!inferenceFile.current || inferenceFile.current.id !== inferenceId) {
          inferenceFile.current = inferenceInList || null;
          if (inferenceFile.current) {
            const reformedFile = processInferenceResult(inferenceFile.current);
            loadLabels(reformedFile);
            setPdfFile(reformInferenceToPdfFile(reformedFile));
          }
        }
      }
    };
    setupInferenceFile();
  }, [inferenceList, inferenceId, loadLabels, algorithm]);

  useEffect(() => {
    if (inferenceFile.current && inferenceList?.length) {
      const index = inferenceList.findIndex((it) => it.id === inferenceFile.current?.id);
      let prevId = null;
      let nextId = null;
      if (index > 0) {
        for (var i = index - 1; i > -1; i--) {
          if (isFinished(inferenceList[i])) {
            prevId = inferenceList[i].id;
            break;
          }
        }
      }
      if (index < inferenceList.length - 1) {
        for (let i = index + 1; i < inferenceList.length; i++) {
          if (isFinished(inferenceList[i])) {
            nextId = inferenceList[i].id;
            break;
          }
        }
      }
      setPrevId(prevId);
      setNextId(nextId);
    }
  }, [inferenceFile, inferenceList]);

  useEffect(() => {
    fetchInferences(true);
  }, [fetchInferences, ids]);

  return (
    <>
      {error ? (
        <ErrorText>Something went wrong. We could not find the documents you were looking for.</ErrorText>
      ) : (
        <>
          <TopBar
            title={(inferenceFile.current && inferenceFile.current.fileName) || ''}
            closeFunction={() => {
              inferenceFile && inferenceFile.current?.model && history.push(`/document-recognition`);
            }}
            prevId={prevId}
            nextId={nextId}
            baseUrl={`/document-recognition/detail`}
          />
          <DetailContainer>
            <CustomSider width={380} border="right" style={{ paddingTop: '1.25rem' }}>
              {inferenceFile?.current?.inferenceResult.result?.tables && (
                <ResultTables inferenceFile={inferenceFile.current} />
              )}
              {inferenceFile?.current?.inferenceResult.result?.properties && (
                <ResultProperties inferenceFile={inferenceFile.current} pubdemo={false} />
              )}
            </CustomSider>
            <Layout>
              <Content>
                <div className={'pdf-layout'} style={{ width: '100%' }}>
                  <PdfContainer
                    file={pdfFile}
                    labels={globalState.labels.filter((label) => label.exists || !label.reliable)}
                    showNonExisting={false}
                    onAnnotationChange={() => {}}
                    publicDemo={true}
                  />
                </div>
              </Content>
            </Layout>
            <CustomSider width={400} border="left" /* style={{ paddingTop: '1.25rem' }} */>
              <Results
                onAccept={() => {}}
                onDelete={() => {}}
                onEdit={() => {}}
                datasetMode={false}
                beautifyLabels={false}
                inTable={true}
                publicDemo={true}
              />
            </CustomSider>
          </DetailContainer>
        </>
      )}
    </>
  );
};
